type NetworkStatus = 'online' | 'offline' | 'syncing';
type NetworkListener = (status: NetworkStatus) => void;

export class NetworkService {
    private _status: NetworkStatus = 'online';
    private _listeners: NetworkListener[] = [];
    private _heartbeatInterval: any = null;
    private _serverUrl: string = '';

    constructor() {
        this._status = navigator.onLine ? 'online' : 'offline';

        window.addEventListener('online', () => {
            this.setStatus('online');
        });

        window.addEventListener('offline', () => {
            this.setStatus('offline');
        });
    }

    get isOnline(): boolean {
        return this._status === 'online' || this._status === 'syncing';
    }

    get isSyncing(): boolean {
        return this._status === 'syncing';
    }

    get status(): NetworkStatus {
        return this._status;
    }

    setServerUrl(url: string) {
        this._serverUrl = url;
    }

    setStatus(status: NetworkStatus) {
        if (this._status !== status) {
            const prev = this._status;
            this._status = status;
            this._listeners.forEach(fn => {
                try { fn(status); } catch (e) { console.error('[NetworkService] Listener error:', e); }
            });
        }
    }

    onStatusChange(listener: NetworkListener): () => void {
        this._listeners.push(listener);
        return () => {
            this._listeners = this._listeners.filter(fn => fn !== listener);
        };
    }

    startHeartbeat(intervalMs: number = 30000) {
        this.stopHeartbeat();
        this._heartbeatInterval = setInterval(async () => {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 5000);
                const url = this._serverUrl ? `${this._serverUrl}/api/selection/ping` : '/api/selection/ping';
                await fetch(url, {
                    method: 'HEAD',
                    cache: 'no-store',
                    signal: controller.signal
                });
                clearTimeout(timeout);
                if (this._status === 'offline') {
                    this.setStatus('online');
                }
            } catch {
                if (this._status === 'online') {
                    this.setStatus('offline');
                }
            }
        }, intervalMs);
    }

    stopHeartbeat() {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }
}

let _networkInstance: NetworkService | null = null;

export function getNetworkService(): NetworkService {
    if (!_networkInstance) {
        _networkInstance = new NetworkService();
    }
    return _networkInstance;
}
